import { useState, useEffect } from 'react';
import { listDeadLetters, retryDeadLetter, dismissDeadLetter, DeadLetterEvent } from '../api/admin-client';
import ConfirmDialog from '../components/ConfirmDialog';

type ActionTarget = { event: DeadLetterEvent; type: 'retry' | 'dismiss' } | null;

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 2) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function eventTypeBadge(t?: string) {
  if (!t) return <span className="badge badge-gray">unknown</span>;
  if (t.startsWith('checkout.session')) return <span className="badge badge-blue">{t}</span>;
  if (t.startsWith('charge.'))          return <span className="badge badge-amber">{t}</span>;
  return <span className="badge badge-gray">{t}</span>;
}

// ─── Tooltip wrapper ──────────────────────────────────────────────────────────

function Tooltip({ text, children }: { text: string; children: React.ReactNode }) {
  return (
    <span className="tooltip-wrap" title={text}>
      {children}
    </span>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function StripeDeadLetter() {
  const [events, setEvents] = useState<DeadLetterEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [endpointMissing, setEndpointMissing] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  // Action state
  const [actionTarget, setActionTarget] = useState<ActionTarget>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState('');

  async function load() {
    setLoading(true);
    setError('');
    try {
      const data = await listDeadLetters();
      setEvents(data);
      setEndpointMissing(false);
    } catch (err: any) {
      if (err?.status === 404 || err?.status === 501 || err?.code === 'not_found') {
        setEndpointMissing(true);
        setError('');
      } else {
        setError(err?.message ?? 'Failed to load dead-letter queue');
      }
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  function toggleExpand(event_id: string) {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(event_id)) next.delete(event_id);
      else next.add(event_id);
      return next;
    });
  }

  async function executeAction() {
    if (!actionTarget) return;
    setActionLoading(true);
    setActionError('');
    try {
      if (actionTarget.type === 'retry') {
        await retryDeadLetter(actionTarget.event.event_id);
      } else {
        await dismissDeadLetter(actionTarget.event.event_id);
      }
      setEvents(es => es.filter(e => e.event_id !== actionTarget.event.event_id));
      setActionTarget(null);
    } catch (err: any) {
      setActionError(err?.message ?? 'Action failed');
    } finally {
      setActionLoading(false);
    }
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
        <div className="page-title" style={{ marginBottom: 0 }}>Stripe Dead-Letter Inspector</div>
        <button
          className="btn btn-ghost btn-sm"
          onClick={load}
          disabled={loading}
          style={{ marginLeft: 'auto' }}
          title="Refresh dead-letter queue"
        >
          {loading ? <span className="spinner" /> : '↻'} Refresh
        </button>
      </div>

      {/* Backend stub banner */}
      {endpointMissing && (
        <div className="stub-banner">
          Endpoints <code>GET /admin/stripe/dead-letters</code>,{' '}
          <code>POST /admin/stripe/dead-letters/:id/retry</code>, and{' '}
          <code>POST /admin/stripe/dead-letters/:id/dismiss</code> are not yet exposed via HTTP.
          <br /><br />
          Backend ticket: Sprint 24 — expose <code>listDeadLetteredStripeEvents()</code> and retry/dismiss
          from payment-service over the admin HTTP layer.
          <br /><br />
          The underlying logic (<code>retryDeadLetteredWebhook</code>, <code>markStripeEventDeadLettered</code>)
          already exists in <code>server/src/payments/payment-service.mjs</code>.
        </div>
      )}

      {error && <div className="error-banner">{error}</div>}

      {!loading && !endpointMissing && events.length === 0 && !error && (
        <div className="empty-state">
          <div style={{ fontSize: 32, marginBottom: 12 }}>✅</div>
          <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 }}>
            No dead-lettered events
          </div>
          <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
            All Stripe webhooks processed successfully.
          </div>
        </div>
      )}

      {events.length > 0 && (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Event ID</th>
                <th>Type</th>
                <th>Received</th>
                <th>Dead-Lettered</th>
                <th>Retries</th>
                <th>Last Error</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {events.map(ev => (
                <>
                  <tr key={ev.event_id}>
                    <td>
                      <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => toggleExpand(ev.event_id)}
                        title="Click to toggle raw Stripe payload"
                      >
                        <span className="text-mono" style={{ fontSize: 11 }}>
                          {ev.event_id.slice(0, 12)}…
                        </span>
                        {expanded.has(ev.event_id) ? ' ▲' : ' ▼'}
                      </button>
                    </td>
                    <td>{eventTypeBadge(ev.event_type)}</td>
                    <td className="text-muted">{timeAgo(ev.received_at)}</td>
                    <td className="status-error">{timeAgo(ev.dead_lettered_at)}</td>
                    <td>
                      <Tooltip text={ev.retry_count >= 5 ? 'Max retries reached. Investigate last error before retrying.' : `${ev.retry_count} automatic retries attempted`}>
                        <span className={ev.retry_count >= 5 ? 'status-error' : 'status-warn'}>
                          {ev.retry_count}
                        </span>
                      </Tooltip>
                    </td>
                    <td style={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <span title={ev.last_error ?? ''} className="text-muted">
                        {ev.last_error ? ev.last_error.slice(0, 80) : '—'}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <Tooltip text="Re-queue this event for processing. Server calls retryDeadLetteredWebhook. Safe to retry — idempotency key prevents double-processing.">
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={() => setActionTarget({ event: ev, type: 'retry' })}
                          >
                            Retry
                          </button>
                        </Tooltip>
                        <Tooltip text="Permanently remove from dead-letter queue. No refund or fulfillment will occur. Use only if the event is truly invalid or already manually handled.">
                          <button
                            className="btn btn-sm btn-ghost"
                            onClick={() => setActionTarget({ event: ev, type: 'dismiss' })}
                          >
                            Dismiss
                          </button>
                        </Tooltip>
                      </div>
                    </td>
                  </tr>
                  {expanded.has(ev.event_id) && (
                    <tr key={`${ev.event_id}-expanded`}>
                      <td colSpan={7} style={{ padding: 0 }}>
                        <pre className="raw-payload">
                          {JSON.stringify(ev.payload_json ?? {}, null, 2)}
                        </pre>
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {actionError && <div className="error-banner mt-16">{actionError}</div>}

      {/* Retry confirm */}
      <ConfirmDialog
        open={actionTarget?.type === 'retry'}
        title="Retry Dead-Lettered Event"
        description={[
          `Re-queue Stripe event ${actionTarget?.event.event_id} for processing.`,
          `This calls retryDeadLetteredWebhook on the server.`,
          `Event type: ${actionTarget?.event.event_type ?? 'unknown'}.`,
          `Retry count so far: ${actionTarget?.event.retry_count ?? 0}.`,
          `Idempotency key prevents double-processing if the event was partially handled.`,
        ].join(' ')}
        confirmLabel="Retry Event"
        variant="default"
        loading={actionLoading}
        fields={[]}
        fieldValues={{}}
        onConfirm={executeAction}
        onCancel={() => setActionTarget(null)}
      />

      {/* Dismiss confirm — danger variant: requires explicit acknowledgement */}
      <ConfirmDialog
        open={actionTarget?.type === 'dismiss'}
        title="Dismiss Dead-Lettered Event"
        description={[
          `Permanently remove event ${actionTarget?.event.event_id} from the dead-letter queue.`,
          `No refund or fulfillment will occur.`,
          `Use only if the event is truly invalid (e.g. test mode event, already manually reconciled).`,
          `This action cannot be undone from the admin UI.`,
        ].join(' ')}
        confirmLabel="Dismiss (No Recovery)"
        variant="danger"
        loading={actionLoading}
        fields={[]}
        fieldValues={{}}
        onConfirm={executeAction}
        onCancel={() => setActionTarget(null)}
      />
    </div>
  );
}
